import { useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, FileText, Upload, X, Check } from 'lucide-react';
import { z } from 'zod';
import { Meta } from '@/components/seo/Meta';
import { Button } from '@/components/ui/Button';
import { Input, Textarea, FieldWrapper } from '@/components/ui/Input';
import { Checkbox } from '@/components/ui/Checkbox';
import { FadeIn } from '@/components/motion/FadeIn';
import { toast } from '@/components/ui/Toast';
import { useRoleBySlug } from '@/lib/cms';
import { submitApplication, uploadCv } from '@/lib/edge-functions';

const applicationSchema = z.object({
  full_name: z.string().min(2, 'Please enter your full name.'),
  email: z.string().email('Please enter a valid email.'),
  phone: z.string().optional().or(z.literal('')),
  linkedin_url: z.string().url('Must be a valid URL.').optional().or(z.literal('')),
  portfolio_url: z.string().url('Must be a valid URL.').optional().or(z.literal('')),
  cover_letter: z.string().max(2000, 'Keep it under 2000 characters.').optional().or(z.literal('')),
  // FormData returns strings; we accept 'true' / '' from the override.
  consent: z
    .union([z.literal('true'), z.boolean()])
    .refine((v) => v === true || v === 'true', {
      message: 'You must agree before submitting.',
    }),
});

const MAX_FILE_BYTES = 5 * 1024 * 1024;
const ACCEPTED_MIME = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];

export function ApplyPage() {
  const { slug } = useParams<{ slug: string }>();
  const { data: role } = useRoleBySlug(slug);

  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [cvFile, setCvFile] = useState<File | null>(null);
  const [cvError, setCvError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  // Radix's Checkbox.Root is not a real form control, so FormData can't see
  // it. Track consent as controlled state and merge it into the schema input.
  const [consent, setConsent] = useState(false);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setErrors({});
    setCvError(null);
    const form = e.currentTarget;
    const data = Object.fromEntries(new FormData(form).entries()) as Record<string, string>;
    // Override consent from controlled state — the value Radix submits is empty.
    data.consent = consent ? 'true' : '';
    const parsed = applicationSchema.safeParse(data);
    if (!parsed.success) {
      const next: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        const key = issue.path[0];
        if (typeof key === 'string' && !next[key]) next[key] = issue.message;
      }
      setErrors(next);
      return;
    }

    setSubmitting(true);
    let cvPath: string | undefined;
    if (cvFile) {
      setUploading(true);
      const up = await uploadCv(cvFile);
      setUploading(false);
      if ('error' in up) {
        setCvError(up.error);
        setSubmitting(false);
        return;
      }
      cvPath = up.path;
    }

    const result = await submitApplication({
      role_slug: slug,
      full_name: parsed.data.full_name,
      email: parsed.data.email,
      phone: parsed.data.phone || undefined,
      linkedin_url: parsed.data.linkedin_url || undefined,
      portfolio_url: parsed.data.portfolio_url || undefined,
      cover_letter: parsed.data.cover_letter || undefined,
      cv_path: cvPath,
      consent: parsed.data.consent === true || parsed.data.consent === 'true',
    });
    setSubmitting(false);

    if (!result.ok) {
      toast.error(result.error);
      return;
    }
    setSubmitted(true);
    toast.success("Application sent. We'll be in touch within 5 business days.");
    form.reset();
    setConsent(false);
    setCvFile(null);
  }

  function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    setCvError(null);
    const f = e.target.files?.[0];
    if (!f) {
      setCvFile(null);
      return;
    }
    if (f.size > MAX_FILE_BYTES) {
      setCvError('CV must be ≤ 5 MB.');
      setCvFile(null);
      return;
    }
    if (!ACCEPTED_MIME.includes(f.type)) {
      setCvError('CV must be a PDF or Word document.');
      setCvFile(null);
      return;
    }
    setCvFile(f);
  }

  if (submitted) {
    return (
      <>
        <Meta title="Application received" path={`/careers/${slug ?? ''}/apply`} />
        <section className="flex min-h-[70vh] items-center justify-center pt-20">
          <div className="container-narrow text-center">
            <div className="mx-auto inline-flex h-12 w-12 items-center justify-center rounded-full bg-gold-500/10 text-gold-500">
              <Check className="h-6 w-6" />
            </div>
            <h1 className="mt-6 display-serif text-display-l text-fg">Application received.</h1>
            <p className="mt-4 text-fg-muted">
              Thank you for applying. We'll review your application and get back to you within 5 business days.
            </p>
            <div className="mt-6 flex justify-center gap-3">
              <Button asChild variant="secondary">
                <Link to="/careers">All roles</Link>
              </Button>
              <Button asChild>
                <Link to="/">Back to home</Link>
              </Button>
            </div>
          </div>
        </section>
      </>
    );
  }

  return (
    <>
      <Meta title={`Apply — ${role?.title ?? 'Role'}`} path={`/careers/${slug ?? ''}/apply`} />
      <main className="pt-24 pb-12 sm:pt-28 md:pt-32 md:pb-20">
        <section className="container-narrow">
          <FadeIn>
            <Link
              to={`/careers/${slug ?? ''}`}
              className="inline-flex items-center gap-1 text-sm text-fg-muted transition-colors hover:text-gold-500"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> Back to role
            </Link>
          </FadeIn>
          <FadeIn delay={0.05}>
            <h1 className="mt-6 display-serif text-display-l text-fg">Apply for this role</h1>
          </FadeIn>
          {role && (
            <FadeIn delay={0.1}>
              <p className="mt-3 text-base text-fg-muted sm:text-lg">
                {role.title}
                {role.location ? ` · ${role.location}` : ''}
              </p>
            </FadeIn>
          )}

          <FadeIn delay={0.15}>
            <form onSubmit={onSubmit} className="mt-10 space-y-5 sm:mt-12">
              <FieldWrapper id="full_name" label="Full name" required error={errors.full_name}>
                <Input id="full_name" name="full_name" required autoComplete="name" />
              </FieldWrapper>
              <FieldWrapper id="email" label="Email" required error={errors.email}>
                <Input id="email" name="email" type="email" required autoComplete="email" />
              </FieldWrapper>
              <FieldWrapper id="phone" label="Phone" hint="Optional" error={errors.phone}>
                <Input id="phone" name="phone" type="tel" autoComplete="tel" />
              </FieldWrapper>
              <div className="grid gap-5 sm:grid-cols-2">
                <FieldWrapper id="linkedin_url" label="LinkedIn" hint="Optional" error={errors.linkedin_url}>
                  <Input id="linkedin_url" name="linkedin_url" type="url" placeholder="https://linkedin.com/in/…" />
                </FieldWrapper>
                <FieldWrapper id="portfolio_url" label="Portfolio" hint="Optional" error={errors.portfolio_url}>
                  <Input id="portfolio_url" name="portfolio_url" type="url" placeholder="https://…" />
                </FieldWrapper>
              </div>
              <FieldWrapper
                id="cover_letter"
                label="Cover letter"
                hint={`Optional · max 2000 characters · ${2000} chars`}
                error={errors.cover_letter}
              >
                <Textarea id="cover_letter" name="cover_letter" maxLength={2000} rows={6} />
              </FieldWrapper>

              {/* CV UPLOAD */}
              <FieldWrapper
                id="cv"
                label="CV / Résumé"
                hint="PDF or Word · max 5 MB · uploaded privately to our HR inbox"
                error={cvError ?? undefined}
              >
                {!cvFile ? (
                  <label
                    htmlFor="cv"
                    className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-md border border-dashed border-border-strong bg-bg-elevated px-6 py-10 text-center transition-colors hover:border-gold-500/40"
                  >
                    <Upload className="h-5 w-5 text-gold-500" />
                    <span className="text-sm font-medium text-fg">Click to upload</span>
                    <span className="text-xs text-fg-subtle">PDF, DOC, DOCX · up to 5 MB</span>
                    <input
                      id="cv"
                      type="file"
                      accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                      onChange={onFile}
                      className="sr-only"
                    />
                  </label>
                ) : (
                  <div className="flex items-center justify-between gap-3 rounded-md border border-border bg-bg-elevated px-4 py-3">
                    <div className="flex items-center gap-3">
                      <FileText className="h-5 w-5 text-gold-500" />
                      <div>
                        <p className="text-sm font-medium">{cvFile.name}</p>
                        <p className="text-xs text-fg-subtle">{(cvFile.size / 1024).toFixed(1)} KB</p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setCvFile(null)}
                      aria-label="Remove file"
                      className="inline-flex h-8 w-8 items-center justify-center rounded-md text-fg-muted hover:bg-bg-subtle hover:text-fg"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                )}
              </FieldWrapper>

              <label className="flex items-start gap-3 rounded-md border border-border bg-bg-elevated p-4">
                <Checkbox
                  id="consent"
                  name="consent"
                  required
                  checked={consent}
                  onCheckedChange={(c) => setConsent(c === true)}
                />
                <span className="text-sm text-fg-muted">
                  I agree to Vonlof storing my application data and contacting me about this role.
                </span>
              </label>
              {errors.consent && <p className="text-xs text-danger">{errors.consent}</p>}

              <div className="flex flex-wrap items-center gap-3 pt-2">
                <Button type="submit" size="lg" loading={submitting || uploading}>
                  {uploading ? 'Uploading CV…' : submitting ? 'Submitting…' : 'Submit application'}
                </Button>
                <Button asChild type="button" variant="ghost" size="lg">
                  <Link to={`/careers/${slug ?? ''}`}>Cancel</Link>
                </Button>
              </div>
            </form>
          </FadeIn>
        </section>
      </main>
    </>
  );
}